import { useEffect, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import { Icons } from '../components/shared/Icons'
import { useAuth } from '../context/AuthContext'
import './DraftPages.css'

export default function PaymentSuccess() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { user, subscription } = useAuth()

  const [paymentType, setPaymentType] = useState(null)
  const [bankDetails, setBankDetails] = useState(null)
  const [instructions, setInstructions] = useState([])

  useEffect(() => {
    // Check if this is a bank transfer (passed via state)
    if (location.state?.type === 'bank_transfer') {
      setPaymentType('bank_transfer')
      setBankDetails(location.state.bankDetails)
      setInstructions(location.state.instructions || [])
    } else if (searchParams.get('session_id')) {
      // This is a Stripe redirect
      setPaymentType('stripe')
    } else {
      // Assume Paysera or general success
      setPaymentType('online')
    }
  }, [location.state, searchParams])

  return (
    <div className="draft-page">
      <section className="draft-hero payment-result-section">
        <div className="container">
          <ScrollReveal>
            <motion.div
              className="payment-result-card success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="result-icon success">
                <Icons.check size={48} />
              </div>

              {paymentType === 'bank_transfer' ? (
                <>
                  <h1 className="result-title">
                    <GradientText gradient="solar">Registration Complete!</GradientText>
                  </h1>
                  <p className="result-subtitle">
                    Your account has been created. Please complete your bank transfer to activate your subscription.
                  </p>

                  {bankDetails && (
                    <div className="bank-details-card">
                      <h3>Bank Transfer Details</h3>
                      <div className="bank-detail-row">
                        <span className="label">Bank Name:</span>
                        <span className="value">{bankDetails.bank_name}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="label">Account Holder:</span>
                        <span className="value">{bankDetails.account_holder}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="label">IBAN:</span>
                        <span className="value copyable">
                          {bankDetails.iban}
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(bankDetails.iban)}
                            className="copy-btn"
                          >
                            <Icons.copy size={16} />
                          </button>
                        </span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="label">BIC/SWIFT:</span>
                        <span className="value">{bankDetails.bic}</span>
                      </div>
                      <div className="bank-detail-row highlight">
                        <span className="label">Reference:</span>
                        <span className="value copyable">
                          {bankDetails.reference}
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(bankDetails.reference)}
                            className="copy-btn"
                          >
                            <Icons.copy size={16} />
                          </button>
                        </span>
                      </div>
                      <div className="bank-detail-row total">
                        <span className="label">Amount:</span>
                        <span className="value">EUR {bankDetails.amount}</span>
                      </div>
                    </div>
                  )}

                  <div className="instructions-box">
                    <h4>Important Instructions</h4>
                    <ul>
                      <li>Include the reference number in your payment description</li>
                      <li>Your subscription will be activated within 1-2 business days after we receive the payment</li>
                      <li>You will receive an email confirmation once your subscription is active</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="result-title">
                    <GradientText gradient="solar">Payment Successful!</GradientText>
                  </h1>
                  <p className="result-subtitle">
                    Thank you for your purchase! Your AI4Solar subscription is now active.
                  </p>

                  <div className="success-details">
                    {user && (
                      <div className="detail-item">
                        <Icons.user size={20} />
                        <span>Account: {user.email}</span>
                      </div>
                    )}
                    {subscription && (
                      <>
                        <div className="detail-item">
                          <Icons.package size={20} />
                          <span>Plan: {subscription.plan?.display_name || 'Professional'}</span>
                        </div>
                        <div className="detail-item">
                          <Icons.zap size={20} />
                          <span>Size: {subscription.mw_amount} MW</span>
                        </div>
                      </>
                    )}
                    <div className="detail-item">
                      <Icons.check size={20} />
                      <span>Status: Active</span>
                    </div>
                  </div>
                </>
              )}

              <div className="result-actions">
                <GlowButton variant="solar" href="/">
                  Go to Dashboard
                  <Icons.arrowRight size={20} />
                </GlowButton>
                <GlowButton variant="outline" href="/features">
                  Explore Features
                </GlowButton>
              </div>

              <p className="result-footer">
                A confirmation email has been sent to your inbox.
                <br />
                Questions? Contact us at <a href="mailto:support@ai4solar.com">support@ai4solar.com</a>
              </p>
            </motion.div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
